/**
 * DisclosureOS - AI Module Hooks
 * Drop-in hooks for every module to call the central AI Intelligence Layer.
 * 
 * Usage:
 *   import { useAIQuality, useAIEmissionFactor } from '../hooks/ai_module_hooks';
 *   const { check, batchCheck } = useAIQuality("voluntary_fw");
 *   const result = await check({ datapoint_id: "dp1", value: 42850 });
 */

import { useState, useCallback, useRef } from 'react';
import { apiFetch } from '../services/api_service';


const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Shared auth fetch
async function aiFetch(agent, action, payload = {}, moduleId = 'unknown') {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/v1/ai/${agent}/${action}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'X-Module-ID': moduleId,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail?.detail || err.detail || `AI ${agent}/${action} failed`);
  }
  return res.json();
}

async function aiGet(path) {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/v1/ai${path}`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) throw new Error(`AI GET ${path} failed`);
  return res.json();
}

// ===== Hook Factory =====
function useAIAgent(agentId, moduleId) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const mid = useRef(moduleId);

  const call = useCallback(async (action, payload) => {
    setLoading(true);
    setError(null);
    try {
      const data = await aiFetch(agentId, action, payload, mid.current);
      setResult(data);
      return data;
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  return { call, loading, error, result };
}

// ===== AI-01: Data Quality Agent =====
export function useAIQuality(moduleId = 'unknown') {
  const { call, loading, error, result } = useAIAgent('quality', moduleId);
  return {
    check: (payload) => call('check', payload),
    batchCheck: (payload) => call('batch_check', payload),
    history: (payload) => call('history', payload),
    explain: (payload) => call('explain', payload),
    loading, error, result,
  };
}

// ===== AI-02: Emission Factor Agent =====
export function useAIEmissionFactor(moduleId = 'unknown') {
  const { call, loading, error, result } = useAIAgent('ef', moduleId);
  return {
    match: (payload) => call('match', payload),
    search: (payload) => call('search', payload),
    explain: (payload) => call('explain', payload),
    alternatives: (payload) => call('alternatives', payload),
    loading, error, result,
  };
}

// ===== AI-03: Scope 3 Agent =====
export function useAIScope3(moduleId = 'unknown') {
  const { call, loading, error, result } = useAIAgent('scope3', moduleId);
  return {
    estimate: (payload) => call('estimate', payload),
    batchEstimate: (payload) => call('batch_estimate', payload),
    uncertainty: (payload) => call('uncertainty', payload),
    breakdown: (payload) => call('breakdown', payload),
    loading, error, result,
  };
}

// ===== AI-07: Disclosure Drafter =====
export function useAIDraft(moduleId = 'unknown') {
  const { call, loading, error, result } = useAIAgent('draft', moduleId);
  return {
    generate: (payload) => call('generate', payload),
    improve: (payload) => call('improve', payload),
    translate: (payload) => call('translate', payload),
    compare: (payload) => call('compare', payload),
    loading, error, result,
  };
}

// ===== AI-14: Consistency Checker =====
export function useAIConsistency(moduleId = 'unknown') {
  const { call, loading, error, result } = useAIAgent('consistency', moduleId);
  return {
    check: (payload) => call('check', payload),
    checkPair: (payload) => call('check_pair', payload),
    explain: (payload) => call('explain', payload),
    resolve: (payload) => call('resolve', payload),
    loading, error, result,
  };
}

// ===== AI-05: Cross-Framework Mapper =====
export function useAICrossMap(moduleId = 'unknown') {
  const { call, loading, error, result } = useAIAgent('crossmap', moduleId);
  return {
    suggest: (payload) => call('suggest', payload),
    confirm: (payload) => call('confirm', payload),
    reject: (payload) => call('reject', payload),
    explain: (payload) => call('explain', payload),
    loading, error, result,
  };
}

// ===== Agent Chain =====
export function useAIChain(moduleId = 'unknown') {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (steps) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/v1/ai/chain`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'X-Module-ID': moduleId,
        },
        body: JSON.stringify({ steps }),
      });
      if (!res.ok) throw new Error('Chain execution failed');
      return res.json();
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  return { execute, loading, error };
}

// ===== Health & Agents List =====
export function useAIHealth() {
  const [health, setHealth] = useState(null);
  const [agents, setAgents] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const [h, a] = await Promise.all([
        aiGet('/health'),
        aiGet('/agents'),
      ]);
      setHealth(h);
      setAgents(a?.agents || []);
      return { health: h, agents: a?.agents };
    } catch {
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { health, agents, fetchHealth, loading };
}
